import React from 'react';
import { Typography, Box } from '@mui/material';

const DenseFormAssistantMcpArticle = () => (
  <div className="blog-full-article">
    <Typography component="p" className="blog-prose blog-dropcap">
      Some products are not a handful of fields on a single page. They are <em>configuration surfaces</em>:
      dozens of tabs, hundreds of inputs, nested rules, and values that only make sense in context. People
      who live in those screens still get lost. New teammates take weeks to learn what each field means.
      And when something needs to change, the cost of clicking the wrong control is high.
    </Typography>

    <Typography component="p" className="blog-prose">
      The pattern I keep coming back to is an <strong>in-app assistant</strong> that is first-class in the
      product — not a chatbot bolted onto a help center. In <strong>ask mode</strong> it can explain any
      input, tab, or current value. In <strong>edit mode</strong> it can update those inputs. Both modes
      talk to the rest of the system through <strong>MCP servers</strong>, not through improvised SQL or
      hidden admin APIs. The servers write the database <em>deterministically</em> and return structured
      results the UI can trust.
    </Typography>

    <Box className="blog-pullquote">
      <Typography component="p">
        The model should never be the source of truth for a write. Tools are. The database only changes
        when a named mutation succeeds.
      </Typography>
    </Box>

    <figure className="blog-arch-figure">
      <img
        src="/images/form-assistant-mcp-architecture.png"
        alt="Architecture: multi-tab UI and assistant on top, query and mutation MCP servers in the middle, database at the bottom"
        className="blog-arch-img"
      />
      <figcaption>
        Ask mode routes to a query MCP server. Edit mode routes to a mutation MCP server. Both return
        structured results; only the mutation path is allowed to write.
      </figcaption>
    </figure>

    <Box className="blog-arch" aria-hidden="false">
      <Typography className="blog-eyebrow">Layered view</Typography>
      <Typography variant="h2" className="blog-h2" style={{ marginTop: 8 }}>
        The same architecture, labeled
      </Typography>
      <Box className="arch-stack">
        <Box className="arch-layer arch-layer-ui">
          <span className="arch-layer-kicker">Product</span>
          <strong>Multi-tab UI · hundreds of inputs</strong>
          <span>Assistant panel: Ask · Edit</span>
        </Box>
        <span className="arch-arrow">tool calls</span>
        <Box className="arch-split">
          <Box className="arch-layer arch-layer-mcp">
            <span className="arch-layer-kicker">Ask mode</span>
            <strong>Query MCP</strong>
            <span>Explain · search schema · read values</span>
          </Box>
          <Box className="arch-layer arch-layer-mcp arch-layer-write">
            <span className="arch-layer-kicker">Edit mode</span>
            <strong>Mutation MCP</strong>
            <span>Validate · write · return results</span>
          </Box>
        </Box>
        <span className="arch-arrow">deterministic I/O</span>
        <Box className="arch-layer arch-layer-db">
          <span className="arch-layer-kicker">Persistence</span>
          <strong>Database</strong>
          <span>Transactional, idempotent writes · structured results</span>
        </Box>
      </Box>
    </Box>

    <Box className="blog-divider">
      <span>The problem</span>
    </Box>

    <Box className="blog-section-block">
      <Typography className="blog-eyebrow">Why a generic chatbot fails here</Typography>
      <Typography variant="h2" className="blog-h2">
        Context is the product
      </Typography>
      <Typography component="p" className="blog-prose">
        A dense form is a graph: field A on the Pricing tab constrains field B on Risk; a toggle on
        Compliance hides an entire subsection. If the assistant only sees a screenshot or a pasted
        question, it will hallucinate labels, invent allowed values, and miss the tab the user is on.
      </Typography>
      <Typography component="p" className="blog-prose">
        So the assistant is not a search box over documentation. It is a <strong>client of the same
        domain model</strong> the screen is built from. Tabs, field ids, current values, validation
        rules, and permissions are tools — not prompt stuffing.
      </Typography>
    </Box>

    <Box className="blog-stat-row">
      <Box className="blog-stat-cell">
        <span className="blog-stat-num">2</span>
        <span className="blog-stat-label">MCP servers: query (read) and mutation (write)</span>
      </Box>
      <Box className="blog-stat-cell">
        <span className="blog-stat-num">Ask / Edit</span>
        <span className="blog-stat-label">explicit modes so writes cannot happen by accident</span>
      </Box>
      <Box className="blog-stat-cell">
        <span className="blog-stat-num">1</span>
        <span className="blog-stat-label">write path: named tools, transactions, returned results</span>
      </Box>
    </Box>

    <Box className="blog-divider">
      <span>Ask mode</span>
    </Box>

    <Box className="blog-section-block">
      <Typography className="blog-eyebrow">Read path</Typography>
      <Typography variant="h2" className="blog-h2">
        The Query MCP server
      </Typography>
      <Typography component="p" className="blog-prose">
        Ask mode is read-only. The model can call tools such as <em>get field definition</em>,{' '}
        <em>search fields by name</em>, <em>get current values for a tab</em>, and{' '}
        <em>explain validation</em>. Those tools hit the Query MCP server, which reads from the
        database (or a read replica / projection) and returns JSON the assistant can quote.
      </Typography>
      <ul className="blog-styled-list">
        <li>
          <span className="blog-li-arrow">1</span>
          <span className="blog-li-content">
            <strong>Schema as a tool</strong>
            <span>
              Field ids, labels, types, allowed enums, and which tab they live on — so the model does
              not guess names.
            </span>
          </span>
        </li>
        <li>
          <span className="blog-li-arrow">2</span>
          <span className="blog-li-content">
            <strong>Live values as a tool</strong>
            <span>
              Answers like “what is the current risk limit?” come from the database, not from the
              last message in the chat.
            </span>
          </span>
        </li>
        <li>
          <span className="blog-li-arrow">3</span>
          <span className="blog-li-content">
            <strong>No side effects</strong>
            <span>
              The Query server cannot INSERT, UPDATE, or DELETE. That constraint is in the server
              implementation, not a suggestion in a system prompt.
            </span>
          </span>
        </li>
      </ul>
    </Box>

    <Box className="blog-divider">
      <span>Edit mode</span>
    </Box>

    <Box className="blog-section-block">
      <Typography className="blog-eyebrow">Write path</Typography>
      <Typography variant="h2" className="blog-h2">
        The Mutation MCP server
      </Typography>
      <Typography component="p" className="blog-prose">
        Edit mode is a different contract. The user has opted in. The assistant can propose updates
        (“set pricing.discount_pct to 12 on this record”), but the Mutation MCP server is what
        actually changes state. It validates against the same rules the UI uses, applies the write
        in a transaction, and <strong>returns the result</strong> — new values, rejected fields,
        and any downstream fields that changed because of the update.
      </Typography>
      <Box className="blog-highlight">
        <Typography component="p">
          Deterministic means: the same authorized tool call with the same payload produces the same
          database outcome. Idempotency keys, explicit field ids, and transactional writes — not
          “the model decided to update a few related things.”
        </Typography>
      </Box>
      <Box className="blog-steps">
        <Box className="blog-step">
          <span className="blog-step-num">1</span>
          <span className="blog-step-body">
            <strong>Propose</strong>
            <p>
              The assistant maps language to a typed payload: record id, field ids, new values,
              optional idempotency key.
            </p>
          </span>
        </Box>
        <Box className="blog-step">
          <span className="blog-step-num">2</span>
          <span className="blog-step-body">
            <strong>Validate</strong>
            <p>
              The mutation server runs schema, permission, and cross-tab rules. Invalid fields fail
              closed and are returned, not silently skipped.
            </p>
          </span>
        </Box>
        <Box className="blog-step">
          <span className="blog-step-num">3</span>
          <span className="blog-step-body">
            <strong>Commit and return</strong>
            <p>
              One transaction. The tool result includes committed values so the UI can refresh the
              exact tabs and inputs that changed.
            </p>
          </span>
        </Box>
      </Box>
    </Box>

    <Box className="blog-divider">
      <span>Why two servers</span>
    </Box>

    <Box className="blog-section-block">
      <Typography className="blog-eyebrow">Blast radius</Typography>
      <Typography variant="h2" className="blog-h2">
        Split read and write on purpose
      </Typography>
      <Typography component="p" className="blog-prose">
        You could expose one MCP server with both tools. Two servers are a better operational
        boundary: different auth, different rate limits, different audit logs, and the ability to
        disable mutations without taking Q&amp;A down. Ask mode never even sees write tools, so a
        confused model cannot “helpfully” persist a change.
      </Typography>
      <Box className="blog-card-grid">
        <Box className="blog-mini-card">
          <span className="blog-mini-card-icon">🔍</span>
          <h4>Query MCP</h4>
          <p>Read replica friendly. High volume. Safe to give to every authenticated user in ask mode.</p>
        </Box>
        <Box className="blog-mini-card">
          <span className="blog-mini-card-icon">✎</span>
          <h4>Mutation MCP</h4>
          <p>Edit-mode only. Audited. Same validators as the form. Returns structured commit results.</p>
        </Box>
        <Box className="blog-mini-card">
          <span className="blog-mini-card-icon">🗄</span>
          <h4>Database</h4>
          <p>The only place state lives. Writes are named operations, not generated queries from the LLM.</p>
        </Box>
        <Box className="blog-mini-card">
          <span className="blog-mini-card-icon">🖥</span>
          <h4>UI refresh</h4>
          <p>Tool results patch the open tabs so the screen and the assistant never disagree.</p>
        </Box>
      </Box>
    </Box>
  </div>
);

export default DenseFormAssistantMcpArticle;
